import { Trash2, Upload } from "lucide-react";
import { useRef, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

/** A picture control for something that is not a person: a preview slot,
 *  Upload, and Remove while there is something to remove. */
export function ImageUpload({
  preview,
  hasImage,
  uploading = false,
  removing = false,
  onUpload,
  onRemove,
  hint,
}: {
  preview: ReactNode;
  hasImage: boolean;
  uploading?: boolean;
  removing?: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  hint?: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-4">
      {preview}
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Spinner data-slot="icon" /> : <Upload data-slot="icon" />}
            {uploading ? "Uploading…" : hasImage ? "Replace picture" : "Upload picture"}
          </Button>
          {hasImage && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove} disabled={removing}>
              <Trash2 data-slot="icon" /> Remove
            </Button>
          )}
        </div>
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
